import React, { useState, useEffect, useRef, useCallback, } from "react";
import Tour from "../TourInterface.ts";
import Parent from "../parentInterface.ts";
import DynamicTab from "../../extras/DynamicTab.tsx";
import AxiosInstance, { refreshParentChildrenBookings, pollParentSyncStatus } from "../../../utils/Axios.ts";
import StyledDataGrid from "./StyledDataGrid.tsx";
import { Link, Paper } from "@mui/material";
import { 
  GridRowSelectionModel, 
  GridRenderEditCellParams, 
  GridRenderCellParams,
  GridFilterModel,
  GridSortModel,
  GridPaginationModel
} from "@mui/x-data-grid";
import ActivitySelectEditor from "./ActivitySelectEditor.tsx";
import visibilityStates from "./visibilityStates.ts";
import ReportOverlay from "../ReportOverlay.tsx";
import BulkActionOverlay from "./BulkActionOverlay.tsx";
import ParentInfoCard from "./ParentInfoCard.tsx";
import useParentSyncPoll from "../useParentSyncPoll.ts";

interface Activity {
  id: number;
  value: string;
  label: string;
}

const DataGridContainer = ({ pk }: { pk: string }) => {
  const gridContainerRef = useRef(null);
  const [parent, setParent] = useState<Parent | null>(null);
  const [tours, setTours] = useState<Tour[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [error, setError] = useState<string | null>(null);
  const [columnVisibility, setColumnVisibility] = useState(visibilityStates.default);
  const [reportOpen, setReportOpen] = useState(false);
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [activityOptions, setActivityOptions] = useState<Activity[]>([]);
  const [filteredActivityOptions, setFilteredActivityOptions] = useState<Activity[]>([]);
  const [activitySearchTerm, setActivitySearchTerm] = useState<string>('');

  // Add state for DataGrid persistence
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ 
    pageSize: 25, 
    page: 0 
  });
  const [columnSizes, setColumnSizes] = useState<{ [field: string]: number }>({});
  const [isRefreshingChildren, setIsRefreshingChildren] = useState(false);
  const [lastRefreshClickAt, setLastRefreshClickAt] = useState<number | null>(null);
  const [refreshStatusText, setRefreshStatusText] = useState("");

  const fetchTours = useCallback(async () => {
    try {
      const parentData = await AxiosInstance.get(`/parent/${pk}/`);
      const response = await AxiosInstance.get(`/tour/${pk}/`);
      const sortedTours = response.data.sort((a, b) => {
        // Handle null or undefined time_from values
        if (!a.time_from) return 1;
        if (!b.time_from) return -1;

        // First compare by time_from
        const timeComparison = a.time_from.localeCompare(b.time_from);

        // If times are equal, compare by name
        if (timeComparison === 0) {
          // Handle null or undefined name values
          if (!a.name) return 1;
          if (!b.name) return -1;
          const nameComparison = a.name.localeCompare(b.name);

          // If names are also equal, compare by bus_sign
          if (nameComparison === 0) {
        // Handle null or undefined bus_sign values
        if (!a.bus_sign) return 1;
        if (!b.bus_sign) return -1;
        return a.bus_sign.localeCompare(b.bus_sign);
          }

          return nameComparison;
        }

        // If times are different, return that comparison result
        return timeComparison;
      });
      setParent(parentData.data);
      setTours(sortedTours);
      setError(null); // Reset error on successful fetch

      const activityResponse = await AxiosInstance.get('activity_supplier/');
      // Add an empty option for clearing the selection
      const options = [
        { id: null, value: "", label: "" },
        ...activityResponse.data.results.map((activity: {id: number, name: string}) => ({
          id: activity.id,
          value: activity.name,
          label: activity.name
        }))
      ];
      setActivityOptions(options);
    } catch (err) {
      setError("Failed to fetch tours.");
    }
  }, [pk]);

  const refreshCooldownMs = 60 * 1000;
  const refreshDisabled = Boolean(
    isRefreshingChildren ||
      (lastRefreshClickAt && Date.now() - lastRefreshClickAt < refreshCooldownMs)
  );

  const handleRefreshChildren = async () => {
    try {
      setIsRefreshingChildren(true);
      setRefreshStatusText("Refreshing child bookings...");
      setLastRefreshClickAt(Date.now());
      const response = await refreshParentChildrenBookings(pk);
      if (response.status === 202) {
        const result = await pollParentSyncStatus(pk);
        if (result.sync_status === "failed" && result.sync_error) {
          setRefreshStatusText(result.sync_error);
          setError(result.sync_error);
        } else {
          setRefreshStatusText(`Last refresh: ${new Date().toLocaleString()}`);
        }
      } else {
        setRefreshStatusText(`Last refresh: ${new Date().toLocaleString()}`);
      }
      await fetchTours();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Refresh failed.";
      setRefreshStatusText(message);
      setError(message);
    } finally {
      setIsRefreshingChildren(false);
    }
  };

  useEffect(() => {
    fetchTours();
  }, [pk, fetchTours]);

  useParentSyncPoll(pk, fetchTours, setParent);

  const handleActivitySearch = async (searchTerm: string) => {
    setActivitySearchTerm(searchTerm);

    if (searchTerm.length > 0) {
      try {
        const response = await AxiosInstance.get('activity_supplier/', {
          params: { search: searchTerm }
        });

        const fetchedOptions = [
          { id: null, value: "", label: "" }, // Empty option
          ...response.data.results.map((activity: {id: number, name: string}) => ({
            id: activity.id,
            value: activity.name,
            label: activity.name
          }))
        ];

        setFilteredActivityOptions(fetchedOptions);
      } catch (error) {
        console.error("Error fetching activity options:", error);
        setFilteredActivityOptions(activityOptions); // Fall back to base options on error
      }
    } else {
      // Reset to default options when search term is cleared
      setFilteredActivityOptions(activityOptions);
    }
  };

  // Initialize filteredActivityOptions in useEffect
  useEffect(() => {
    setFilteredActivityOptions(activityOptions);
  }, [activityOptions]);

  // Pre-process tours to ensure activity fields are properly formatted
  const processedTours = tours.map(tour => {
    // Make a copy of the tour
    const processedTour = { ...tour };

    // Ensure activity_1_name is not undefined
    if (processedTour.activity_1_name === undefined) {
      processedTour.activity_1_name = "";
    }

    // Ensure activity_2_name is not undefined
    if (processedTour.activity_2_name === undefined) {
      processedTour.activity_2_name = "";
    }

    return processedTour;
  });

  // Update columns to use dynamic widths from columnSizes state
  const columns = [
    { field: "bus_sign", headerName: "Bus Sign", width: columnSizes["bus_sign"] || 150, editable: true },
    { field: "booking_id", headerName: "Id", width: columnSizes["booking_id"] || 80 },
    {
      field: "booking_link",
      headerName: "Booking Link",
      width: columnSizes["booking_link"] || 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const bookingUrl = `https://travelconnect.corivo.io/bookings/${params.row.booking_id}/`;
        return (
          <Link
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Booking
          </Link>
        );
      }
    },
    {
      field: "prime_portal_link",
      headerName: "TripDesk",
      width: columnSizes["prime_portal_link"] || 220,
      sortable: false,
      editable: true,
      renderCell: (params: GridRenderCellParams) => {
        const primePortalUrl = params.row.prime_portal_link;
        if (!primePortalUrl) {
          return "";
        }
        return (
          <Link
            href={primePortalUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open TripDesk
          </Link>
        );
      }
    },
    {
      field: "travel_documents_link",
      headerName: "Travel Documents",
      width: columnSizes["travel_documents_link"] || 220,
      sortable: false,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        const travelDocsUrl = params.row.travel_documents_link;
        if (!travelDocsUrl) {
          return "";
        }
        return (
          <Link
            href={travelDocsUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open documents
          </Link>
        );
      }
    },
    { field: "ready", headerName: "Ready", width: columnSizes["ready"] || 100, type: "boolean", editable: true },
    { field: "bus_ready", headerName: "Bus Ready", width: columnSizes["bus_ready"] || 100, type: "boolean", editable: true },
    { field: "guide_ready", headerName: "Guide Ready", width: columnSizes["guide_ready"] || 100, type: "boolean", editable: true },
    { field: "supplier_called", headerName: "Supplier Called", width: columnSizes["supplier_called"] || 140, type: "boolean", editable: true },
    { field: "name", headerName: "Tour Name", width: columnSizes["name"] || 200 },
    { field: "tour_code", headerName: "Tour Code", width: columnSizes["tour_code"] || 150, editable: true },
    { field: "time_from", headerName: "Time From", width: columnSizes["time_from"] || 150 },
    { field: "time_to", headerName: "Time To", width: columnSizes["time_to"] || 150 },
    { field: "max_size", headerName: "Max Size", width: columnSizes["max_size"] || 100, editable: true },
    { field: "size", headerName: "Sold", width: columnSizes["size"] || 100 },
    { field: "final_pax", headerName: "Pax", width: columnSizes["final_pax"] || 100, editable: true },
    { field: "child_num", headerName: "Child Num", width: columnSizes["child_num"] || 100, type: "number", editable: true },
    { field: "infant_num", headerName: "Infant", width: columnSizes["infant_num"] || 100, type: "number", editable: true },
    { field: "escort", headerName: "Escort", width: columnSizes["escort"] || 100, type: "number", editable: true },
    { field: "actual_departure_time", headerName: "Departure", width: columnSizes["actual_departure_time"] || 150, type: "string", editable: true },
    { field: "actual_arrival_time", headerName: "Arrival", width: columnSizes["actual_arrival_time"] || 150, type: "string", editable: true },
    { field: "park_spot", headerName: "Parking Spot", width: columnSizes["park_spot"] || 150, editable: true },
    { field: "bus_name", headerName: "Bus", width: columnSizes["bus_name"] || 150, editable: true },
    { field: "bus_size", headerName: "Bus Size", width: columnSizes["bus_size"] || 150, editable: true },
    { field: "bus_company", headerName: "Bus Company", width: columnSizes["bus_company"] || 150, editable: true },
    { field: "driver", headerName: "Driver", width: columnSizes["driver"] || 150, editable: true },
    { field: "driver_phone", headerName: "Driver Phone", width: columnSizes["driver_phone"] || 150, editable: true },
    { field: "guide_name", headerName: "Guide", width: columnSizes["guide_name"] || 150, editable: true },
    { field: "guide_phone", headerName: "Guide Phone", width: columnSizes["guide_phone"] || 150, editable: true },
    { field: "guide_language", headerName: "Guide Language", width: columnSizes["guide_language"] || 150, editable: true },
    { field: "meal", headerName: "Meal", width: columnSizes["meal"] || 150, editable: true },
    {
      field: "activity_1_name",
      headerName: "Activity 1",
      width: columnSizes["activity_1_name"] || 150,
      editable: true,
      type: "singleSelect",
      valueOptions: filteredActivityOptions,
      renderEditCell: (params: GridRenderEditCellParams) => (
        <ActivitySelectEditor
          {...params}
          valueOptions={filteredActivityOptions}
          onSearchChange={handleActivitySearch}
          searchTerm={activitySearchTerm}
          setSearchTerm={setActivitySearchTerm}
        />
      ),
      filterOperators: [
        {
          label: 'Contains',
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              return value && value.toLowerCase().includes(filterItem.value.toLowerCase());
            };
          },
          InputComponent: 'GridFilterInputValue',
        },
      ]
    },
    { field: "activity_1_time", headerName: "Activity 1 Time", width: columnSizes["activity_1_time"] || 150, editable: true },
    { field: "activity_1_phone", headerName: "Activity 1 Phone", width: columnSizes["activity_1_phone"] || 150 },
    {
      field: "activity_2_name",
      headerName: "Activity 2",
      width: columnSizes["activity_2_name"] || 150,
      editable: true,
      type: "singleSelect",
      valueOptions: filteredActivityOptions,
      renderEditCell: (params: GridRenderEditCellParams) => (
        <ActivitySelectEditor
          {...params}
          valueOptions={filteredActivityOptions}
          onSearchChange={handleActivitySearch}
          searchTerm={activitySearchTerm}
          setSearchTerm={setActivitySearchTerm}
        />
      ),
      filterOperators: [
        {
          label: 'Contains',
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              return value && value.toLowerCase().includes(filterItem.value.toLowerCase());
            };
          },
          InputComponent: 'GridFilterInputValue',
        },
      ]
    },
    { field: "activity_2_time", headerName: "Activity 2 Time", width: columnSizes["activity_2_time"] || 150, editable: true },
    { field: "activity_2_phone", headerName: "Activity 2 Phone", width: columnSizes["activity_2_phone"] || 150 },
    { field: "comment", headerName: "Comment", width: columnSizes["comment"] || 200, editable: true },
    {
      field: "delete_action",
      headerName: "Delete",
      width: columnSizes["delete_action"] || 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const handleDelete = async () => {
          try {
            // Show confirmation dialog
            if (window.confirm("Are you sure you want to delete this tour? This action cannot be undone.")) {
              // Call the delete endpoint
              await AxiosInstance.delete(`/tour/delete/${params.row.id}/`);

              // Remove the deleted tour from state
              const updatedTours = tours.filter(tour => tour.id !== params.row.id);
              setTours(updatedTours);
            }
          } catch (err) {
            setError("Failed to delete tour.");
            console.error("Delete error:", err);
          }
        };

        return (
          <button
            onClick={handleDelete}
            style={{
              backgroundColor: "#d32f2f", // Red color
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "6px 12px",
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            Delete!
          </button>
        );
      }
    },
    {
      field: "color_choice",
      headerName: "Color Choice",
      width: columnSizes["color_choice"] || 150,
      type: "singleSelect",
      editable: true,
      valueOptions: [
        { value: "", label: "" }, // Empty option
        { value: 0, label: "White" },
        { value: 1, label: "Red" },
        { value: 2, label: "Orange" },
        { value: 3, label: "Light Green" },
        { value: 4, label: "Blue" },
        { value: 5, label: "Purple" },
        { value: 6, label: "Pink" },
        { value: 7, label: "Brown" },
        { value: 8, label: "Yellow" },
        { value: 9, label: "Sea Green" },
        { value: 10, label: "Light Blue" },
      ],
    },
  ];

  const parseBusSizeOverride = (value: unknown): number | null => {
    if (value === "" || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const handleRowUpdate = (newRow) => {
    const updatedRows = tours.map((row) =>
      row.id === newRow.id ? { ...row, ...newRow } : row
    );
    setTours(updatedRows);

    const rowForApi = { ...newRow };

    // Find activity IDs if activities are selected
    if (newRow.activity_1_name) {
      const activity1 = activityOptions.find(opt => opt.value === newRow.activity_1_name);
      if (activity1) {
        rowForApi.activity_1 = activity1.id;
      }
    } else {
      rowForApi.activity_1 = null;
    }

    if (newRow.activity_2_name) {
      const activity2 = activityOptions.find(opt => opt.value === newRow.activity_2_name);
      if (activity2) {
        rowForApi.activity_2 = activity2.id;
      }
    } else {
      rowForApi.activity_2 = null;
    }

    delete rowForApi.bus_name;
    delete rowForApi.guide_name;
    delete rowForApi.guide_phone;
    delete rowForApi.bus_size;
    delete rowForApi.travel_documents_link;
    delete rowForApi.prime_portal_link;

    rowForApi.bus_override = String(newRow.bus_name ?? "").trim();
    rowForApi.guide_override = String(newRow.guide_name ?? "").trim();
    rowForApi.guide_phone_override = String(newRow.guide_phone ?? "").trim();
    rowForApi.bus_size_override = parseBusSizeOverride(newRow.bus_size);

    AxiosInstance.patch(`/tour/patch/${newRow.id}/`, rowForApi)
      .catch(() => {
        setError("Failed to update the row on the server.");
      });

    return newRow;
  };

  const handleVisibilityChange = (stateName: keyof typeof visibilityStates) => {
    setColumnVisibility(visibilityStates[stateName]);
  };

  const openReportOverlay = () => {
    setReportOpen(true); // Open the overlay
  };

  const closeReportOverlay = () => {
    setReportOpen(false); // Close the overlay
  };

  const openBulkActionOverlay = () => {
    setBulkActionOpen(true); // Open the overlay
  };

  const closeBulkActionOverlay = () => {
    setBulkActionOpen(false); // Close the overlay
  };

  // Handle column resize
  const handleColumnResize = (params) => {
    setColumnSizes(prev => ({
      ...prev,
      [params.colDef.field]: params.width
    }));
  };

  return (
    <DynamicTab title={parent?.name || "Tours"} favicon="/logos/IT logo.png">

    <ParentInfoCard
      parent={parent}
      onRefreshChildren={handleRefreshChildren}
      isRefreshingChildren={isRefreshingChildren}
      refreshDisabled={refreshDisabled}
      refreshStatusText={refreshStatusText}
    />

    <Paper elevation={3} ref={gridContainerRef} style={{ height: "100%", padding: "16px" }}>
      {error && <div style={{ color: "red" }}>{error}</div>}
      <StyledDataGrid
        rows={processedTours}
        columns={columns}
        rowSelectionModel={rowSelectionModel}
        processRowUpdate={handleRowUpdate}
        setRowSelectionModel={setRowSelectionModel}
        maxHeight="100%"
        handleBulkAction={(selectedRows) => {
          console.log("Bulk action on rows:", selectedRows);
        }}
        handleVisibilityChange={handleVisibilityChange}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        openReportOverlay={openReportOverlay}
        openBulkActionOverlay={openBulkActionOverlay}
        
        // Add controlled state props
        filterModel={filterModel}
        onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        paginationModel={paginationModel}
        onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
        onColumnResize={handleColumnResize}
        
        // Optional: Add these for better UX
        pageSizeOptions={[25, 50, 100]}
        disableColumnFilter={false}
        disableColumnSelector={false}
        disableDensitySelector={false}
      />
      <ReportOverlay open={reportOpen} onClose={closeReportOverlay} parentTourId={pk} />
      <BulkActionOverlay open={bulkActionOpen} onClose={closeBulkActionOverlay} rowSelectionModel={rowSelectionModel} tours={tours} setTours={setTours} />
    </Paper>
    </DynamicTab>
  );
};

export default DataGridContainer;
